import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { X } from '@phosphor-icons/react';
import type { BriefAsset, Version, WorkbenchCard } from '@mybusinessmate/schema';
import { briefRows } from '../../lib/workbench-brief';
import { taskQuery, versionsQuery } from '../../lib/tasks-api';
import { TASK_TYPE_META } from '../../lib/task-display';
import { agoText, dayText, dueText, laneOf, noteLabel, taskRef } from '../../lib/workbench-display';
import { Icon } from '../Icon';
import { AssetImage } from '../tasks/asset-image';
import { buttonClassName } from '../ui/button';
import { useModalDialog } from '../ui/dialog';
import { IconButton } from '../ui/icon-button';
import { ProgressBar } from '../ui/progress-bar';
import { Skeleton } from '../ui/skeleton';
import { StatusTag } from '../ui/status-tag';
import { ErrorState } from '../ui/state-views';
import { PriorityPill } from './priority-pill';

const THUMBS = 3;

const SECTION = 'text-label-small text-text-tertiary uppercase';

/** What happened to the newest version, in a line: "Sent to client 1 d ago · approved by Sana". */
function versionLine(version: Version): string {
  if (version.isPublished) {
    return `Sent to client ${agoText(version.publishedAt)}${version.publishedByName ? ` · approved by ${version.publishedByName}` : ''}`;
  }
  if (version.internalStatus === 'pending_lead_review') return `Sent for QA ${agoText(version.updatedAt)}`;
  if (version.internalStatus === 'changes_requested') return `Sent back ${agoText(version.leadReviewedAt)}`;
  return 'Still a draft';
}

function Thumbs({ assets }: { assets: BriefAsset[] }) {
  const shown = assets.slice(0, THUMBS);
  return (
    <div className="grid grid-cols-3 gap-(--mbm-space-2)">
      {shown.map((asset) => (
        <div
          key={asset.id}
          className="relative h-(--mbm-size-peek-thumb) overflow-hidden rounded-(--mbm-component-radius-peek-thumb) bg-surface-field"
        >
          <AssetImage asset={asset} />
          {assets.length > THUMBS && asset === shown[THUMBS - 1] ? (
            <span className="absolute right-(--mbm-space-2) bottom-(--mbm-space-2) rounded-full bg-action-primary px-(--mbm-space-2) text-label-small text-text-on-dark">
              +{assets.length - THUMBS}
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function Body({ card }: { card: WorkbenchCard }) {
  const task = useQuery(taskQuery(card.taskId));
  const versions = useQuery(versionsQuery(card.taskId));
  const latest = versions.data?.[0] ?? null;
  const rows = task.data?.brief ? briefRows(task.data.brief.details) : [];
  const label = noteLabel(card);
  const back = card.sentBack;

  if (task.isError || versions.isError) {
    return (
      <ErrorState
        title="Couldn’t load the details"
        onRetry={() => {
          void task.refetch();
          void versions.refetch();
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-(--mbm-space-5-5)">
      {label && back ? (
        <div className="flex flex-col gap-(--mbm-space-1) rounded-(--mbm-component-radius-peek-note) bg-board-peek-note px-(--mbm-space-3-5) py-(--mbm-space-3)">
          <span className="text-label-small text-tag-amber-text uppercase">{label}</span>
          {back.note ? <p className="text-body-compact text-text-primary">{back.note}</p> : null}
          <span className="text-body-small text-text-secondary">
            {[
              back.byName ? `${back.byName} (${back.by})` : back.by === 'client' ? 'The client' : 'A lead',
              agoText(back.at),
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
      ) : null}

      {versions.isPending && card.version ? (
        <div className="flex flex-col gap-(--mbm-space-2-5)">
          <Skeleton className="h-(--mbm-space-3-5) w-1/3" />
          <Skeleton className="h-(--mbm-size-peek-thumb) w-full" />
        </div>
      ) : latest ? (
        <section
          className="flex flex-col gap-(--mbm-space-2-5)"
          aria-label={`Latest version, v${latest.versionNumber}`}
        >
          <h3 className={SECTION}>Latest version · v{latest.versionNumber}</h3>
          {latest.assets.length > 0 ? <Thumbs assets={latest.assets} /> : null}
          <p className="text-body-small text-text-secondary">{versionLine(latest)}</p>
        </section>
      ) : null}

      <section className="flex flex-col gap-(--mbm-space-2-5)" aria-label="Brief version 0">
        <h3 className={SECTION}>What was asked · Brief v0</h3>
        {task.isPending ? (
          <div className="flex flex-col gap-(--mbm-space-2-5)">
            <Skeleton className="h-(--mbm-space-5) w-full" />
            <Skeleton className="h-(--mbm-space-5) w-5/6" />
            <Skeleton className="h-(--mbm-space-5) w-2/3" />
          </div>
        ) : rows.length > 0 ? (
          <dl className="flex flex-col gap-(--mbm-space-2-5)">
            {rows.map((row) => (
              <div key={row.label} className="flex gap-(--mbm-space-3)">
                <dt className="w-(--mbm-space-9) shrink-0 text-body-small text-text-secondary">{row.label}</dt>
                <dd className="min-w-0 text-body-compact text-text-primary">{row.value}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="text-body-small text-text-secondary">The brief has no details yet.</p>
        )}
      </section>
    </div>
  );
}

/**
 * Figma A05.7d: a look at one task without leaving the board. A 480px sheet from the right over a scrim; Esc or a click
 * on the scrim closes it. The card it came from keeps a dark outline behind it. "Open task" goes to the full page.
 */
export function QuickPeek({ card, onClose }: { card: WorkbenchCard | null; onClose: () => void }) {
  const { ref, dialogProps } = useModalDialog(card !== null, onClose);
  const type = card ? TASK_TYPE_META[card.taskType] : null;
  const lane = card ? laneOf(card.column) : null;
  const due = card ? dueText(card.deadline) : null;

  return (
    <dialog
      ref={ref}
      aria-label={card ? `${card.title}, quick look` : 'Quick look'}
      className="my-0 ml-auto mr-0 h-dvh max-h-none w-[min(var(--mbm-layout-form-drawer-width),100vw)] max-w-none bg-surface-elevated p-0 text-text-primary shadow-(--mbm-shadow-lg) backdrop:bg-overlay-scrim"
      {...dialogProps}
    >
      {card && type && lane && due ? (
        <div className="flex h-full flex-col">
          <header className="flex flex-col gap-(--mbm-space-2-5) border-b border-border-line px-(--mbm-space-6) py-(--mbm-space-5)">
            <div className="flex items-center gap-(--mbm-space-1-5) text-body-small text-text-secondary">
              <Icon icon={type.icon} size="sm" />
              <span className="min-w-0 flex-1 truncate">
                {type.short} · task #{taskRef(card.taskId)}
              </span>
              <IconButton
                icon={X}
                variant="ghost"
                aria-label="Close"
                onClick={onClose}
                className="-mr-(--mbm-space-2-5) -my-(--mbm-space-2-5)"
              />
            </div>
            <h2 className="text-title-sheet text-text-primary">{card.title}</h2>
            <p className="text-body-compact text-text-secondary">
              {[card.businessName, card.projectName].filter(Boolean).join(' · ')}
            </p>
            <div className="flex flex-wrap items-center gap-(--mbm-space-2)">
              <PriorityPill priority={card.priority} />
              <StatusTag label={lane.title} tone={lane.tone} />
              <span
                className={due.urgent ? 'text-body-small text-tag-amber-text' : 'text-body-small text-text-secondary'}
              >
                {card.deadline && due.urgent ? `${due.text} · ${dayText(card.deadline)}` : due.text}
              </span>
            </div>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto px-(--mbm-space-6) py-(--mbm-space-5)">
            <Body card={card} />
          </div>

          <footer className="flex h-(--mbm-size-peek-footer) shrink-0 items-center gap-(--mbm-space-3) border-t border-border-line px-(--mbm-space-6)">
            <div className="flex flex-1 flex-col gap-(--mbm-space-1-5)">
              <span className="text-label-small text-text-primary">
                {card.progress}% · {card.workUnits} u
              </span>
              <div className="w-(--mbm-layout-peek-bar-width)">
                <ProgressBar value={card.progress} tone="dark" label={`${card.title} progress`} />
              </div>
            </div>
            <Link
              to="/subdomain/admin/tasks/$taskId"
              params={{ taskId: card.taskId }}
              className={buttonClassName({ size: 'dialog' })}
            >
              Open task
            </Link>
          </footer>
        </div>
      ) : null}
    </dialog>
  );
}
